import { Injectable, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Leg } from '@approot/models';
import { ConfigService, TradesCaptureService } from '@approot/services';
import { Utilities } from '@approot/utilities';
import { getAttributeControlFromKey } from '../utilities/leg-attribute-utilities';
import { makeTextCustomColor } from '../utilities/leg.utilities';
import { forceCashSettlementChange } from '../utilities/trade-attribute-utilities';
import { LegHelperService } from './leg-helper.service';

@Injectable({
  providedIn: 'root'
})
export class LegTradeFormListenersService {
  constructor(
    private legHelperService: LegHelperService,
    private renderer: Renderer2,
    private tradesCaptureService: TradesCaptureService,
    private configService: ConfigService
  ) {}

  public tradeFormListeners(leg: Leg): void {
    const form = this.legHelperService.form;
    this.premiumListener(form);
    this.midPvListener(form);
    this.valuationAdjustmentListener(form);
    this.forceCashSettlementListener(form);
    this.counterPartiesListeners(form, leg);
    this.genericCurrencyListener(
      form,
      'retrocessionFeeInPremiumCurrency',
      leg,
      true
    );
    this.genericCurrencyListener(
      form,
      'marginAmountInPremiumCurrency',
      leg,
      true
    );
    this.genericCurrencyListener(form, 'tradingMargin', leg, true);
  }

  private premiumListener(form: FormGroup): void {
    if (form.get('premium')) {
      this.legHelperService.subSink.sink = form
        .get('premium')
        .valueChanges.subscribe(() => {
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'premium'),
            form.get('premium'),
            ' ' + form.get('premiumCurrency').value,
            !!form.get('premiumCurrency').value,
            'currency',
            this.renderer
          );
        });
    }
  }

  private genericCurrencyListener(
    form: FormGroup,
    name: string,
    leg: Leg,
    highlightNegatives = false
  ): void {
    if (!form.get(name)) {
      return;
    }
    this.legHelperService.subSink.sink = form
      .get(name)
      .valueChanges.subscribe(() => {
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + name),
          form.get(name),
          ' ' + form.get('premiumCurrency').value,
          !!form.get('premiumCurrency').value,
          'currency',
          this.renderer
        );
        if (highlightNegatives) {
          const val = Utilities.formattedNumberToNumber(form.get(name).value);
          if (val < 0) {
            makeTextCustomColor(name, Utilities.orangeTextColor, leg);
          } else {
            makeTextCustomColor(name, null, leg);
          }
        }
      });
  }

  private midPvListener(form: FormGroup): void {
    if (form.get('midPv')) {
      this.legHelperService.subSink.sink = form
        .get('midPv')
        .valueChanges.subscribe(() => {
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'midPv'),
            form.get('midPv'),
            ' ' + form.get('midPvCurrency').value,
            !!form.get('midPvCurrency').value,
            'currency',
            this.renderer
          );
        });
    }
  }

  private valuationAdjustmentListener(form: FormGroup): void {
    if (form.get('valuationAdjustment')) {
      this.legHelperService.subSink.sink = form
        .get('valuationAdjustment')
        .valueChanges.subscribe(() => {
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'valuationAdjustment'),
            form.get('valuationAdjustment'),
            ' ' + form.get('valuationAdjustmentCurrency').value,
            !!form.get('valuationAdjustmentCurrency').value,
            'currency',
            this.renderer
          );
        });
    }
  }

  private forceCashSettlementListener(form: FormGroup): void {
    if (form.get('forceCashSettlement')) {
      this.legHelperService.subSink.sink = form
        .get('forceCashSettlement')
        .valueChanges.subscribe(val => {
          forceCashSettlementChange(val, form);
        });
    }
  }

  private counterPartiesListeners(form: FormGroup, leg: Leg): void {
    if (form.get('counterPartyA') && form.get('counterPartyB')) {
      this.legHelperService.subSink.sink = form
        .get('counterPartyA')
        .valueChanges.subscribe(counterParty => {
          if (form.get('counterPartyB').value === counterParty) {
            form.get('counterPartyB').setValue('', { emitEvent: false });
          }
          if (
            this.configService.userSettings().parent
              .getCounterpartiesFromBackEnd
          ) {
            this.updateTradeViewCounterPartiesFromBackend(
              'B',
              counterParty,
              leg,
              form
            );
          }
          this.legHelperService.onChangeInCounterParty();
        });

      this.legHelperService.subSink.sink = form
        .get('counterPartyB')
        .valueChanges.subscribe(counterParty => {
          if (form.get('counterPartyA').value === counterParty) {
            form.get('counterPartyA').setValue('', { emitEvent: false });
          }
          if (
            this.configService.userSettings().parent
              .getCounterpartiesFromBackEnd
          ) {
            this.updateTradeViewCounterPartiesFromBackend(
              'A',
              counterParty,
              leg,
              form
            );
          }
          this.legHelperService.onChangeInCounterParty();
        });
    }
  }

  private updateTradeViewCounterPartiesFromBackend(
    side: 'A' | 'B',
    counterParty: string,
    leg: Leg,
    form: FormGroup
  ) {
    if (!counterParty) {
      return;
    }
    const reset = '(reset)';
    const key = 'counterParty' + side;
    const attribute = getAttributeControlFromKey(
      key,
      leg.attributesAsFormControls
    );
    if (counterParty === reset) {
      const otherSide = side === 'A' ? 'B' : 'A';
      this.tradesCaptureService
        .getCounterPartyList(side)
        .subscribe(counterPartyList => {
          attribute.options = [...counterPartyList, reset];
        });
      this.tradesCaptureService
        .getCounterPartyList(otherSide)
        .subscribe(counterPartyList => {
          const otherKey = 'counterParty' + otherSide;
          const otherAttribute = getAttributeControlFromKey(
            otherKey,
            leg.attributesAsFormControls
          );
          otherAttribute.options = [...counterPartyList, reset];
        });
      form.get('counterParty' + otherSide).setValue('', { emitEvent: false });
      form.get('counterParty' + side).setValue('', { emitEvent: false });
      return;
    }

    this.tradesCaptureService
      .getCounterPartyList(side, counterParty)
      .subscribe(counterPartyList => {
        attribute.options = [...counterPartyList, reset];
        form.get(key).setValue(counterPartyList[0], { emitEvent: false });
      });
  }
}
